import { Autocomplete, Stack, TextField } from '@mui/material';
import { PerUnitProduct } from '@vy/services';
import {
  FixedProduct,
  INTEGRATION_PROVIDERS,
  NetSuiteProductEntity,
  ProductIntegrationEntity,
  UsageProduct,
  XeroProduct,
} from '@vy/types';
import { fromNullToUndefined, isNotNullOrUndefined, isNullOrUndefined } from '@vy/utils';
import { FC, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { DEBOUNCE_DELAY } from '../../../../../components/design/Input/input-consts';
import { VyDialog } from '../../../../../components/new-design/feedback/VyDialog';
import { useQuickBooksProducts } from '../../../../../hooks/requests/integrations/quickbooks/use-quickbooks-products';
import { usePaddleProducts, useXeroProducts } from '../../../../../hooks/requests/integrations';
import { useModifyExternalProduct } from '../../../../../hooks/requests/integrations/use-modify-external-product';
import { useConnectedIntegrations } from '../../../../../hooks/requests/use-integrations';
import { useUpdateProductIntegrationEntities } from '../../../../../hooks/requests/use-update-product-integration-entities';
import { useDebounce } from '../../../../../hooks/use-debounce';
import { useIntegrationEntities } from '../../../../../hooks/use-integration-entities';
import { queryKeys } from '../../../../../services/query-key/query-keys';
import { runMutation } from '../../../../../utils/vy-mutation';
import {
  ProductErpForm,
  ProductErpFormSchema,
} from '../../../../invoices/CustomerInvoicePage/customer-invoice-page-components/InvoiceActivity/invoice-activity-components/InvoiceActivityTabs/invoice-activity-tabs/InvoiceActivityErpSyncTab/Invoice-activity-erp-sync-tab-components/ErpProductSettingsAccordion/erp-product-settings-accordion-types/product-erp-form-schema';
import {
  buildUpdateLineItemIntegrationEntitiesPayload,
  getProductErpFormDefaultValues,
  mapFormValuesToIntegrationEntity,
} from '../../../../invoices/CustomerInvoicePage/customer-invoice-page-components/InvoiceActivity/invoice-activity-components/InvoiceActivityTabs/invoice-activity-tabs/InvoiceActivityErpSyncTab/Invoice-activity-erp-sync-tab-components/ErpProductSettingsAccordion/erp-product-settings-accordion-utils/erp-product-settings-accordion-form-utils';
import { NetSuiteClassesDropdown } from '../../../../plans/CreatePlanPage/create-plan-page-components/FixedChargeEditDrawer/fixed-charge-edit-drawer-components/components/NetSuiteClassesDropdown';
import { NetSuiteProductsDropdown } from '../../../../plans/CreatePlanPage/create-plan-page-components/FixedChargeEditDrawer/fixed-charge-edit-drawer-components/components/NetSuiteProductsDropdown';

type EditProductErpSettingsDialogProps = {
  open: boolean;
  onClose: () => void;
  productName: string;
  product: FixedProduct | PerUnitProduct | UsageProduct;
  contractId: string;
  customerId?: string;
};

function isQuickBooksIntegration(
  externalIntegration: { type: string; id: string } | null | undefined,
): boolean {
  return externalIntegration?.type === INTEGRATION_PROVIDERS.quickBooks;
}

function isXeroIntegration(externalIntegration: { type: string; id: string } | null | undefined): boolean {
  return externalIntegration?.type === INTEGRATION_PROVIDERS.xero;
}

function isPaddleIntegration(externalIntegration: { type: string; id: string } | null | undefined): boolean {
  return externalIntegration?.type === INTEGRATION_PROVIDERS.paddle;
}

export const EditProductErpSettingsDialog: FC<EditProductErpSettingsDialogProps> = ({
  open,
  onClose,
  productName,
  product,
  contractId,
  customerId,
}) => {
  const queryClient = useQueryClient();
  const { data: connectedIntegrations = [] } = useConnectedIntegrations();

  // Check if product is already connected to an ERP
  const productHasQuickBooks = isQuickBooksIntegration(product.externalIntegration);
  const productHasXero = isXeroIntegration(product.externalIntegration);
  const productHasPaddle = isPaddleIntegration(product.externalIntegration);
  const {
    integrationEntity: netSuiteIntegrationEntity,
    integrationEntityIndex: netSuiteIntegrationEntityIndex,
    integrationEntities: productIntegrationEntities,
  } = useIntegrationEntities<NetSuiteProductEntity>({
    integrationEntities: product.integrationEntities,
    integrationProvider: INTEGRATION_PROVIDERS.netSuite,
  });
  const productHasNetSuite = isNotNullOrUndefined(netSuiteIntegrationEntity);

  const accountHasQuickBooks = connectedIntegrations.includes(INTEGRATION_PROVIDERS.quickBooks);
  const accountHasNetSuite = connectedIntegrations.includes(INTEGRATION_PROVIDERS.netSuite);
  const accountHasXero = connectedIntegrations.includes(INTEGRATION_PROVIDERS.xero);
  const accountHasPaddle = connectedIntegrations.includes(INTEGRATION_PROVIDERS.paddle);

  const isQuickBooks =
    productHasQuickBooks || (accountHasQuickBooks && !productHasNetSuite && !productHasXero && !productHasPaddle);
  const isNetSuite =
    productHasNetSuite || (accountHasNetSuite && !productHasQuickBooks && !productHasXero && !productHasPaddle);
  const isXero =
    productHasXero || (accountHasXero && !productHasQuickBooks && !productHasNetSuite && !productHasPaddle);
  const isPaddle =
    productHasPaddle ||
    (accountHasPaddle && !productHasQuickBooks && !productHasNetSuite && !productHasXero);

  const [quickBooksSearchInput, setQuickBooksSearchInput] = useState<string | undefined>(undefined);
  const [selectedQuickBooksProductId, setSelectedQuickBooksProductId] = useState<string | null>(
    isQuickBooks ? (product.externalIntegration?.id ?? null) : null,
  );
  const debouncedQuickBooksSearch = useDebounce(quickBooksSearchInput, DEBOUNCE_DELAY);
  const { data: { products: quickBooksProducts } = {} } = useQuickBooksProducts(debouncedQuickBooksSearch, {
    keepPreviousData: true,
  });

  const selectedQuickBooksProduct = useMemo(() => {
    if (isNullOrUndefined(quickBooksProducts) || isNullOrUndefined(selectedQuickBooksProductId)) {
      return null;
    }
    return quickBooksProducts.find((product) => product.id === selectedQuickBooksProductId) ?? null;
  }, [quickBooksProducts, selectedQuickBooksProductId]);

  const [xeroSearchInput, setXeroSearchInput] = useState<string | undefined>(undefined);
  const [selectedXeroProductId, setSelectedXeroProductId] = useState<string | null>(
    isXero ? (product.externalIntegration?.id ?? null) : null,
  );
  const debouncedXeroSearch = useDebounce(xeroSearchInput, DEBOUNCE_DELAY);
  const { data: { products: xeroProducts } = {} } = useXeroProducts(debouncedXeroSearch, {
    keepPreviousData: true,
  });

  const selectedXeroProduct = useMemo(() => {
    if (isNullOrUndefined(xeroProducts) || isNullOrUndefined(selectedXeroProductId)) {
      return null;
    }
    return xeroProducts.find((product) => product.id === selectedXeroProductId) ?? null;
  }, [xeroProducts, selectedXeroProductId]);

  const [selectedPaddleProductId, setSelectedPaddleProductId] = useState<string | null>(
    isPaddle ? (product.externalIntegration?.id ?? null) : null,
  );
  const { data: { products: paddleProducts } = {} } = usePaddleProducts({
    keepPreviousData: true,
  });

  const selectedPaddleProduct = useMemo(() => {
    if (isNullOrUndefined(paddleProducts) || isNullOrUndefined(selectedPaddleProductId)) {
      return null;
    }
    return paddleProducts.find((paddleProduct) => paddleProduct.id === selectedPaddleProductId) ?? null;
  }, [paddleProducts, selectedPaddleProductId]);

  const { control, handleSubmit, reset, setValue } = useForm<ProductErpForm>({
    defaultValues:
      getProductErpFormDefaultValues(productIntegrationEntities) ?? ProductErpFormSchema.parse({}),
  });

  const updateProductMutation = useUpdateProductIntegrationEntities({
    onSuccess: async () => {
      await queryClient.invalidateQueries(queryKeys.contractPage(contractId));
      await queryClient.invalidateQueries(queryKeys.plans(contractId));
    },
  });

  const modifyExternalProductMutation = useModifyExternalProduct(product.id, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(queryKeys.contractPage(contractId));
      await queryClient.invalidateQueries(queryKeys.plans(contractId));
    },
  });

  useEffect(() => {
    if (open) {
      if (isNetSuite) {
        const resetFormValues = getProductErpFormDefaultValues(productIntegrationEntities);
        reset(resetFormValues ?? ProductErpFormSchema.parse({}));
      }

      if (isQuickBooks) {
        setSelectedQuickBooksProductId(product.externalIntegration?.id ?? null);
        setQuickBooksSearchInput(undefined);
      }

      if (isXero) {
        setSelectedXeroProductId(product.externalIntegration?.id ?? null);
        setXeroSearchInput(undefined);
      }

      if (isPaddle) {
        setSelectedPaddleProductId(product.externalIntegration?.id ?? null);
      }
    }
  }, [
    open,
    productIntegrationEntities,
    product.id,
    product.externalIntegration,
    isNetSuite,
    isQuickBooks,
    isXero,
    isPaddle,
  ]);

  const handleClose = () => {
    onClose();
    reset();
    setSelectedQuickBooksProductId(null);
    setQuickBooksSearchInput(undefined);
    setSelectedXeroProductId(null);
    setXeroSearchInput(undefined);
    setSelectedPaddleProductId(null);
  };

  const submitQuickBooksForm = async () => {
    await runMutation(
      modifyExternalProductMutation,
      {
        externalId: fromNullToUndefined(selectedQuickBooksProductId),
        integration: INTEGRATION_PROVIDERS.quickBooks,
        productId: product.id,
      },
      {
        pending: 'Updating product ERP settings...',
        success: 'Product ERP settings updated successfully',
        error: 'Failed to update product ERP settings',
      },
      {
        onSuccess: () => {
          onClose();
        },
      },
    );
  };

  const submitXeroForm = async () => {
    await runMutation(
      modifyExternalProductMutation,
      {
        externalId: fromNullToUndefined(selectedXeroProductId),
        integration: INTEGRATION_PROVIDERS.xero,
        productId: product.id,
      },
      {
        pending: 'Updating product ERP settings...',
        success: 'Product ERP settings updated successfully',
        error: 'Failed to update product ERP settings',
      },
      {
        onSuccess: () => {
          onClose();
        },
      },
    );
  };

  const submitPaddleForm = async () => {
    await runMutation(
      modifyExternalProductMutation,
      {
        externalId: fromNullToUndefined(selectedPaddleProductId),
        integration: INTEGRATION_PROVIDERS.paddle,
        productId: product.id,
      },
      {
        pending: 'Updating product ERP settings...',
        success: 'Product ERP settings updated successfully',
        error: 'Failed to update product ERP settings',
      },
      {
        onSuccess: () => {
          onClose();
        },
      },
    );
  };

  const submitNetSuiteForm = handleSubmit(async (formValues) => {
    const netSuiteIntegrationEntity = mapFormValuesToIntegrationEntity(formValues);

    const updatedIntegrationEntities = buildUpdateLineItemIntegrationEntitiesPayload(
      productIntegrationEntities,
      netSuiteIntegrationEntityIndex,
      netSuiteIntegrationEntity,
    );

    await runMutation(
      updateProductMutation,
      {
        productId: product.id,
        integrationEntities: updatedIntegrationEntities as ProductIntegrationEntity[],
      },
      {
        pending: 'Updating product ERP settings...',
        success: 'Product ERP settings updated successfully',
        error: 'Failed to update product ERP settings',
      },
      {
        onSuccess: () => {
          onClose();
        },
      },
    );
  });

  const handleSubmitForm = isPaddle
    ? submitPaddleForm
    : isQuickBooks
      ? submitQuickBooksForm
      : isXero
        ? submitXeroForm
        : submitNetSuiteForm;

  return (
    <VyDialog
      label={productName ?? 'Product'}
      labelDescription="Edit ERP settings"
      open={open}
      onClose={handleClose}
      ctaText="Confirm"
      ctaDisabled={updateProductMutation.isLoading || modifyExternalProductMutation.isLoading}
      onSubmit={handleSubmitForm}
      cancelButtonText="Cancel"
    >
      <Stack sx={{
        gap: 2
      }}>
        {isQuickBooks && (
          <Autocomplete
            options={quickBooksProducts ?? []}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedQuickBooksProduct}
            onChange={(_, value) => setSelectedQuickBooksProductId(value?.id ?? null)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="QB ERP ID"
                onChange={(e) => {
                  if (e.target.value === '') {
                    setQuickBooksSearchInput(undefined);
                  } else {
                    setQuickBooksSearchInput(e.target.value);
                  }
                }}
              />
            )}
          />
        )}
        {isNetSuite && !isQuickBooks && !isXero && (
          <>
            <NetSuiteProductsDropdown
              fieldPath="product"
              control={control}
              showDropdown
              customerId={customerId}
              onChange={(modifiedIntegrationEntity) => {
                if (isNullOrUndefined(modifiedIntegrationEntity)) {
                  setValue('classMetadata', null);
                }
              }}
            />
            <NetSuiteClassesDropdown control={control} fieldPath="classMetadata" />
          </>
        )}
        {isXero && (
          <Autocomplete<XeroProduct>
            options={xeroProducts ?? []}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedXeroProduct}
            onChange={(_, value) => setSelectedXeroProductId(value?.id ?? null)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Xero ERP ID"
                onChange={(e) => {
                  if (e.target.value === '') {
                    setXeroSearchInput(undefined);
                  } else {
                    setXeroSearchInput(e.target.value);
                  }
                }}
              />
            )}
          />
        )}
        {isPaddle && (
          <Autocomplete
            options={paddleProducts ?? []}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedPaddleProduct}
            onChange={(_, value) => setSelectedPaddleProductId(value?.id ?? null)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Paddle Product"
              />
            )}
          />
        )}
      </Stack>
    </VyDialog>
  );
};
